/**
 * Age-Grade & Match-Format Policy Catalog — safeguarding, not presentation.
 *
 * WHY THIS EXISTS
 *
 * Age group was a free-typed field with a hand-written dropdown, and match format
 * did not exist at all. Two consequences, both reported from a real walk of the
 * deployed app:
 *
 *   1. The ladder read U8, U10, U12, U14, U16, U18, U20 — it skipped every odd
 *      bracket (no U9, U11, U13, U15, U17) and carried a U20 that US Soccer does
 *      not field. Hand-typed lists drift; generated ones cannot.
 *   2. A team recorded as "U11B Premier 2" was offered 4-3-3, 4-4-2, 3-5-2 and
 *      4-2-3-1 in the lineup builder — every formation in the codebase was
 *      eleven-a-side — and the builder hardcoded `index < 11` for the starting
 *      side, so a 9v9 team could not even express its lineup.
 *
 * Putting ten-year-olds into an eleven-a-side game is a physical-safety and
 * insurance question long before it is an interface question. So: age group is
 * DERIVED from date of birth, and match format is DERIVED from age group. Neither
 * is typed by a human, and the lineup builder can only offer what the format
 * allows.
 *
 * Shape follows `./jurisdiction.ts`: a typed policy catalog plus pure helpers,
 * with unit tests guarding the invariants. Add a federation by extending
 * `AGE_GRADE_POLICIES` and adding a test case.
 */
/** Players on the pitch per side, including the goalkeeper where one is used. */
export const PLAYERS_ON_PITCH = {
    '4v4': 4,
    '7v7': 7,
    '9v9': 9,
    '11v11': 11,
};
/**
 * Youngest and oldest brackets Kantera generates.
 *
 * US Soccer fields U6 through U19. U20 is NOT generated — it appeared in the old
 * hand-typed list with no rule behind it. It is still ACCEPTED on read (see
 * `isKnownAgeGroup`) because rows carrying it may already exist; it is simply
 * never offered.
 */
export const MIN_AGE_GRADE = 4;
export const MAX_AGE_GRADE = 19;
/**
 * The full ladder, generated from the bounds. This is the fix for the missing
 * odd brackets: there is no list to forget to update.
 */
export const AGE_GRADES = Array.from({ length: MAX_AGE_GRADE - MIN_AGE_GRADE + 1 }, (_, i) => `U${MIN_AGE_GRADE + i}`);
/** Brackets tolerated on read but never offered in a picker. */
const LEGACY_AGE_GRADES = ['U20'];
export const AGE_GRADE_POLICIES = {
    /**
     * US Soccer Player Development Initiatives: 4v4 to U6, 7v7 through U10,
     * 9v9 through U12, 11v11 from U13.
     */
    US_SOCCER: {
        code: 'US_SOCCER',
        label: 'US Soccer',
        seasonCutoff: { month: 5, day: 16 }, // 16 June (month is zero-indexed)
        formatBands: [
            [6, '4v4'],
            [10, '7v7'],
            [12, '9v9'],
            [MAX_AGE_GRADE, '11v11'],
        ],
    },
    /**
     * Wisconsin Youth Soccer Association follows the US Soccer small-sided
     * standards. Kept as its own entry so a WYSA-specific divergence has an
     * obvious home rather than becoming a special case in a helper.
     */
    WYSA: {
        code: 'WYSA',
        label: 'Wisconsin Youth Soccer Association',
        seasonCutoff: { month: 5, day: 16 },
        formatBands: [
            [6, '4v4'],
            [10, '7v7'],
            [12, '9v9'],
            [MAX_AGE_GRADE, '11v11'],
        ],
    },
};
export const DEFAULT_FEDERATION = 'US_SOCCER';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** True for any bracket the system will store, including legacy ones. */
export function isKnownAgeGrade(value) {
    if (!value)
        return false;
    return AGE_GRADES.includes(value) || LEGACY_AGE_GRADES.includes(value);
}
/** The bracket number, or null when the string is not a bracket. */
export function ageGradeNumber(ageGrade) {
    if (!ageGrade)
        return null;
    const m = /^U(\d{1,2})$/.exec(ageGrade.trim().toUpperCase());
    if (!m)
        return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
}
/**
 * Season year for a given date: the year whose season a roster built on that
 * date belongs to. On or after the cutoff, that is next year.
 */
export function seasonYearFor(asOf, federation = DEFAULT_FEDERATION) {
    const { month, day } = AGE_GRADE_POLICIES[federation].seasonCutoff;
    // UTC getters, deliberately. A date of birth and a season cutoff are CALENDAR
    // dates, not instants. Local-time getters make the cutoff move with the
    // server's timezone: '2026-06-16T00:00:00Z' reads as 15 June in any US zone,
    // so the same child would land in different brackets depending on where the
    // API happened to be deployed. Caught by the cutoff-boundary test.
    const isNextSeason = asOf.getUTCMonth() > month || (asOf.getUTCMonth() === month && asOf.getUTCDate() >= day);
    return isNextSeason ? asOf.getUTCFullYear() + 1 : asOf.getUTCFullYear();
}
/**
 * Derive a player's age grade from their date of birth.
 *
 * Returns null rather than a guess when the date is missing, unparseable, or
 * lands outside the ladder — a null is a visible "we do not know", where a
 * fallback bracket would silently place a child in the wrong band.
 *
 * @param dateOfBirth ISO date ('2015-03-04') or anything Date can parse
 * @param opts.asOf   evaluate as of this date (defaults to now); pass it for
 *                    deterministic tests and for back-dated registrations
 */
export function deriveAgeGrade(dateOfBirth, opts = {}) {
    if (!dateOfBirth)
        return null;
    const dob = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
    if (Number.isNaN(dob.getTime()))
        return null;
    const federation = opts.federation ?? DEFAULT_FEDERATION;
    const asOf = opts.asOf ?? new Date();
    // getUTCFullYear for the same reason as seasonYearFor: a '2015-01-01' DOB
    // parses to UTC midnight and reads as 2014 in any negative-offset zone.
    const n = seasonYearFor(asOf, federation) - dob.getUTCFullYear();
    if (n < MIN_AGE_GRADE || n > MAX_AGE_GRADE)
        return null;
    return `U${n}`;
}
/**
 * The format a bracket plays. This is the rule that closes the U11-in-11v11
 * hole: a lineup builder asks this, not the user.
 */
export function matchFormatForAgeGrade(ageGrade, federation = DEFAULT_FEDERATION) {
    const n = ageGradeNumber(ageGrade);
    if (n === null)
        return null;
    for (const [bound, format] of AGE_GRADE_POLICIES[federation].formatBands) {
        if (n <= bound)
            return format;
    }
    // Above the last band (e.g. a legacy U20) the oldest format applies.
    const bands = AGE_GRADE_POLICIES[federation].formatBands;
    return bands[bands.length - 1]?.[1] ?? null;
}
/** Starting-side size for a format. Replaces every hardcoded `11`. */
export function startingSideSize(format) {
    return PLAYERS_ON_PITCH[format];
}
/**
 * Total outfield slots a formation string accounts for, e.g. '4-3-3' -> 10.
 * Excludes the goalkeeper, which formation notation never names.
 */
export function formationOutfieldCount(formation) {
    const parts = formation.trim().split('-');
    if (parts.length < 2)
        return null;
    let total = 0;
    for (const p of parts) {
        if (!/^\d+$/.test(p))
            return null;
        total += Number(p);
    }
    return total;
}
/**
 * Whether the format is played with a dedicated goalkeeper.
 *
 * 4v4 is keeper-less in the US Soccer small-sided standards, so its formation
 * notation accounts for ALL four players rather than outfield-only. Getting this
 * wrong would reject every legal 4v4 shape.
 */
export function hasGoalkeeper(format) {
    return format !== '4v4';
}
/**
 * Whether a formation can be fielded in a given format.
 *
 * Formation notation names the outfield players and leaves the goalkeeper
 * implicit — so 4-3-3 is 10 outfield + 1 GK = 11, legal only at 11v11. At 4v4
 * there is no keeper, so the digits account for the whole side.
 */
export function isFormationAllowedForFormat(formation, format) {
    const size = PLAYERS_ON_PITCH[format];
    if (!size)
        return false;
    const outfield = formationOutfieldCount(formation);
    if (outfield === null)
        return false;
    return outfield + (hasGoalkeeper(format) ? 1 : 0) === size;
}
/**
 * Curated shapes per format, commonest first.
 *
 * The first entry is the DEFAULT for a new team. Team creation used to hardcode
 * `preferredFormation: '4-4-2'` for every team regardless of age, so a U9 side
 * was born holding an eleven-a-side shape — the defect this catalog removes.
 *
 * Every entry is asserted legal for its format by the unit tests, so a typo here
 * fails the build rather than reaching a coach.
 */
export const FORMATIONS_BY_FORMAT = {
    // Keeper-less; digits cover all four players.
    '4v4': ['1-2-1', '2-2', '1-1-2'],
    // 6 outfield + GK.
    '7v7': ['2-3-1', '3-2-1', '2-1-2-1', '1-3-2'],
    // 8 outfield + GK.
    '9v9': ['3-2-3', '3-3-2', '2-3-3', '3-4-1', '2-4-2'],
    // 10 outfield + GK.
    '11v11': ['4-4-2', '4-3-3', '4-2-3-1', '3-5-2', '3-4-3', '5-3-2', '4-5-1'],
};
/** Shapes a picker may offer for a format. Empty for an unknown format. */
export function formationsForFormat(format) {
    if (!format)
        return [];
    return FORMATIONS_BY_FORMAT[format] ?? [];
}
/**
 * The formation a new team of this age grade should start with.
 *
 * Returns null when the grade is unrecognisable — the caller must then leave
 * the formation unset rather than defaulting, which is what put an
 * eleven-a-side shape on a U9 team.
 */
export function defaultFormationForAgeGrade(ageGrade, federation = DEFAULT_FEDERATION) {
    const format = matchFormatForAgeGrade(ageGrade, federation);
    if (!format)
        return null;
    return FORMATIONS_BY_FORMAT[format][0] ?? null;
}
/** Age grades offered in a picker — the generated ladder, never a literal. */
export function selectableAgeGrades() {
    return AGE_GRADES;
}
export function classifyPlacement(playerAgeGrade, teamAgeGrade) {
    const p = ageGradeNumber(playerAgeGrade);
    const t = ageGradeNumber(teamAgeGrade);
    if (p === null || t === null)
        return null;
    if (t === p)
        return 'at_grade';
    return t > p ? 'playing_up' : 'playing_down';
}
/** True when a placement may not be saved without a recorded approval. */
export function placementRequiresApproval(placement) {
    return placement === 'playing_down';
}
//# sourceMappingURL=age-grade.js.map